import {
  FreeCashFlowEntry,
  MeasurementType,
  SlotCategory,
} from 'types/evaluation';
import { isSlotCategory, slotCategoryRecord } from 'utils/slot-categories';
import { parseMomentUtc } from 'utils/dates';
import { filterFreeCashflow } from 'lib/hooks/useFilterFreeCashflow';

/** Per-slot-category cumulative cashflow totals (+ the all-category `total`). */
export type CumulativeCashflowRow = Record<SlotCategory, number> & {
  total: number;
};

/**
 * The Cumulative Cash Flow summary shown below the Cash Flow chart: two windows —
 * the N-year limit (`throughYear`) and the full life (`finalYear`) — each summed
 * per slot category. Shared by the on-screen `FreeCashFlowCumulativeTable` and the
 * content-export footer so both read one computation.
 */
export interface CumulativeCashflowSummary {
  /** The N-year window's end year, clamped to `finalYear`. */
  throughYear: number;
  /** The last year any cashflow occurs (the full-life window's end). */
  finalYear: number;
  /** Cumulative totals through `throughYear`. */
  yearLimit: CumulativeCashflowRow;
  /** Cumulative totals through `finalYear` (the whole series). */
  limitless: CumulativeCashflowRow;
}

// A window wide enough to capture the whole series (the "through final year" row).
const LIMITLESS_YEARS = 100_000;

/**
 * Sums a window's entries per slot category (and overall).
 *
 * @param {FreeCashFlowEntry[]} cashflows - The window's entries.
 * @param {MeasurementType} measurementType - `net` or `gross` (selects the cashflow field).
 * @return {CumulativeCashflowRow} The per-category + total sums.
 */
function sumByCategory(
  cashflows: FreeCashFlowEntry[],
  measurementType: MeasurementType
): CumulativeCashflowRow {
  const row: CumulativeCashflowRow = {
    ...slotCategoryRecord(() => 0),
    total: 0,
  };
  // A row whose category is not in the shared list (an old eval's grouped bucket) is left out of
  // the columns AND the total, so the columns still add up to it.
  cashflows.forEach((cashflow) => {
    if (!isSlotCategory(cashflow.category)) {
      return;
    }
    const value = cashflow[`${measurementType}Cashflow`];
    row[cashflow.category] += value;
    row.total += value;
  });
  return row;
}

/**
 * Computes the Cumulative Cash Flow summary (both windows) from the free-cash-flow
 * entries. Pure — no React — so it feeds both the on-screen table and the export footer.
 *
 * @param {FreeCashFlowEntry[]} freeCashFlows - The unfiltered cashflow entries.
 * @param {string} effectiveDate - The evaluation effective date (UTC ISO).
 * @param {number} noOfYears - The N-year window length (org config).
 * @param {MeasurementType} measurementType - `net` or `gross`.
 * @return {CumulativeCashflowSummary} The two windows' per-category totals + their end years.
 */
export function computeCumulativeCashflowSummary(
  freeCashFlows: FreeCashFlowEntry[],
  effectiveDate: string,
  noOfYears: number,
  measurementType: MeasurementType
): CumulativeCashflowSummary {
  const finalYear =
    freeCashFlows.length > 0
      ? Math.max(
          ...freeCashFlows.map((flow) =>
            Number(parseMomentUtc(flow.date).format('YYYY'))
          )
        )
      : Number(parseMomentUtc(effectiveDate).format('YYYY'));

  const effectiveEndYear = Number(
    parseMomentUtc(effectiveDate).add(noOfYears, 'years').format('YYYY')
  );

  return {
    finalYear,
    throughYear: effectiveEndYear > finalYear ? finalYear : effectiveEndYear,
    yearLimit: sumByCategory(
      filterFreeCashflow(freeCashFlows, effectiveDate, noOfYears),
      measurementType
    ),
    limitless: sumByCategory(
      filterFreeCashflow(freeCashFlows, effectiveDate, LIMITLESS_YEARS),
      measurementType
    ),
  };
}
